import {GigyaModel} from '../GigyaModel';
import {TokenModel} from './TokenModel';
import {AppSettingModel} from '../app-configs/AppSettingModel';
import {BaseModel} from '../base/BaseModel';
import {BaseFilterModel} from '../filter/BaseFilterModel';
import {DistributorsCollection} from '../../collection/users/DistributorsCollection';
import {UserSettingsCollection} from '../../collection/users/UserSettingsCollection';
import {AppSettingCollection} from '../../collection/app-configs/AppSettingCollection';
import {GigyaUtils} from '../../utils/GigyaUtils';
import {CountryUtils} from '../../utils/CountryUtils';
import {RegexUtils} from '../../utils/RegexUtils';
import {UserPasswordValidationModel} from '../UserPasswordValidationModel';
import {CacheUtils as Cache} from '../../utils/CacheUtils';
import {CropUtils} from '../../utils/CropUtils';

import CookieReader from 'cookies-js';
import Raven from 'raven-js';
import {FarmModel} from '../farms/FarmModel';

/**
 * @class UserModel
 * @extends BaseModel
 */
export class UserModel extends BaseModel {


    /**
     * $.Deferred() - set when settings and distributors are requested
     *
     * @readonly
     *
     * @memberOf UserModel
     */
    get depsFetched () {

        return this._depsFetched;
    }

    /**
     * $.Deferred() - set when profile information is requested
     *
     * @readonly
     *
     * @memberOf UserModel
     */
    get profileFetched () {

        return this._profileFetched;
    }

    /**
     * Object - returns language code - country mapping
     *
     *      * @readonly
     *
     * @memberOf UserModel
     */
    get latestTermsAndConditions () {

        return this._latestTermsAndConditions;
    }

    initialize () {

        this.microservice = 'users';
        this.resource = 'users';
        this._depsFetched = null; // used internally to see request state
        this._profileFetched = null; // used internally to see request state

        this.setRavenUserContext();
    }

    defaults () {

        let token = new TokenModel();
        let uuid = ((token.attributes.decoded && token.attributes.decoded.uuid) ? token.attributes.decoded.uuid : '');

        return {
            uuid: uuid,
            token: token,
            settings: new UserSettingsCollection(),
            applicationSettings: new AppSettingCollection([], {filterParams : new BaseFilterModel({
                objectType : _.values(AppSettingModel.OBJECT_TYPE)
            })}),
            distributors: new DistributorsCollection(),
            gigya: new GigyaModel(),
            accessGroups: '',
            password: '',
            email: '',
            firstName: '',
            lastName: '',
            zip: '',
            location: '',
            phone: '',
            mobilePhone: '',
            address: '',
            taxId: '',
            state: '',
            isVerified: '',
            registrationSource: '',
            dob: '',
            isScoutingUser: '',
            inviteCodeUuid: '',
            businessName: ''
        };
    }

    validateProperty (property, attributes, options) {

        switch (property) {

            case 'firstName': {

                return UserModel.validateFirstName(attributes.firstName, options);
            }

            case 'lastName': {

                return UserModel.validateLastName(attributes.lastName, options, attributes.country);
            }

            case 'password': {

                return UserModel.validatePassword(attributes.password, options);
            }

            case 'email': {

                return UserModel.validateEmail(attributes.email, 'email', options);
            }

            case 'dob': {

                return UserModel.validateDob(attributes.dob);
            }

            case 'zip': {

                // Fix for inputmask model validation
                if (!options.country) {
                    options.country = this.getCountry();
                }

                return UserModel.validateZip(attributes.zip, 'zip', options);
            }

            case 'phone': {

                return UserModel.validatePhone(attributes.phone, 'phone', options);
            }

            case 'mobilePhone': {

                return UserModel.validateMobilePhone(attributes.mobilePhone, 'mobilePhone', options);
            }

            case 'address': {

                return UserModel.validateAddress(attributes.address, 'address', options);
            }

            case 'businessName': {

                return UserModel.validateBusinessName(attributes.businessName, 'businessName', options);
            }

            case 'location': {

                return UserModel.validateLocation(attributes.location);
            }

            case 'taxId': {

                return UserModel.validateTaxId(attributes.taxId);
            }

            case 'country': {

                return UserModel.validateCountry(attributes.country);
            }

            case 'language': {

                return UserModel.validateLanguage(attributes.language);
            }
        }
    }

    gigyaLogin () {

        return this.get('gigya').login({
            include: 'profile',
            loginID: this.get('email'),
            password: this.get('password'),
            extraProfileFields: 'address,phones'
        });
    }

    login () {

        let loggedIn = $.Deferred();

        // Clear Local storage
        localStorage.removeItem('SHOP_PACKAGE_UUIDS');
        localStorage.removeItem('SHOP_VOUCHER');
        localStorage.removeItem('CFD_FILTER_GROUP_GENERAL');
        localStorage.removeItem('CFD_FILTER_GROUP_RISK');
        localStorage.removeItem('CFD_FILTER_GROUP_TASK');
        localStorage.removeItem('CFD_FILTER_STATE');

        this.gigyaLogin()
            .done((response)=>{
                this.onLoggedIn(response, loggedIn);
            }).fail((response)=>{
                let errMessage = GigyaUtils.getGigyaErrorMessage(response);

                if (errMessage) {
                    loggedIn.reject(errMessage);
                }
            });

        return loggedIn;
    }

    /**
     * @description This method is used to clear the user password stored in the model for security purpose.
     *
     * @memberOf UserModel
     */
    clearPassword () {

        this.set('password', '');
    }

    onLoggedIn (response, loggedIn) {

        this.set('uuid', response.UID);

        Cache.uuid = response.UID;

        this.exchangeGigyaToken({
                gigyaUuid: response.UID,
                gigyaUuidSignature: response.UIDSignature,
                gigyaSignatureTimestamp: response.signatureTimestamp
            })
            .then(() => {
                return this.fetchDeps();
            },(tokenResponse) => {

                if (tokenResponse && tokenResponse.responseJSON.errorCode === 'AUTH__PROFILE_INCOMPLETE') {

                    // Setting the registration source as scouting user
                    this.set('registrationSource', UserModel.REG_SOURCE.SCOUTING);
                    this.set('firstName', response.profile.firstName);
                    this.set('lastName', response.profile.lastName);
                    this.saveProfileInfo();

                    loggedIn.reject({
                        reason: 'EXISTING_XARVIO_USER',
                        uuid: response.UID,
                        message: ''
                    });
                } else {
                    loggedIn.reject({
                        reason : 'TECH',
                        message : 'Fetching DF_TOKEN failed.'
                    });
                }
            })
            .then(() => {
                return this.fetchProfile({extraProfileFields: 'address,phones,languages', include: 'profile, data, emails', UID: this.get('uuid')});
            },()=>{
                loggedIn.reject({
                    reason : 'TECH',
                    message : 'Fetching settings and distributors failed.'
                });
            })
            .then((resp) => {
                this.setProfileInfo(resp.profile, resp.data, resp.isVerified, resp.emails);
                this.setRavenUserContext();
                loggedIn.resolve(this);
            },() => {
                loggedIn.reject({
                    reason : 'TECH',
                    message : 'Fetching account info failed'
                });
            });
    }

    resetPassword (passResetToken) {

        let passReseted = $.Deferred();

        if(_.isUndefined(passResetToken)){
            passReseted.reject({
                reason: 'TECH',
                message: __('GIGYA_PWRT_IS_NOT_PROVIDED')
            });
        } else {
            this.get('gigya').resetPassword({
                    loginID: this.get('email'),
                    newPassword: this.get('password'),
                    passwordResetToken: passResetToken
            }).then(() => {
                passReseted.resolve();
            },(response) => {
                passReseted.reject(GigyaUtils.getGigyaErrorMessage(response));
            });
        }

        return passReseted;
    }

    requestPasswordReset (country, language) {

        return $.ajax({
            url: (API_PREFIX + '/' + this.microservice + '/passwords'),
            type: 'POST',
            dataType: 'json',
            contentType: 'application/json',
            beforeSend: (jqXHR)  => {
                jqXHR.setRequestHeader('DF_TOKEN', '');
            },
            data: JSON.stringify({
                email: this.get('email'),
                country: country,
                language: language
            })
        });
    }

    setProfileInfo (profile, data = {}, isVerified, emails) {

        this.set('zip', profile.zip || '');
        this.set('email', profile.email);
        this.set('taxId', data.bc_taxID);
        this.set('accessGroups', data.bc_accessGroups);
        this.set('address', profile.address || '');
        this.set('location', profile.city || '');
        this.set('lastName', profile.lastName || '');
        this.set('firstName', profile.firstName || '');

        if (profile.birthDay && profile.birthMonth && profile.birthYear) {
            this.set('dob', moment({d:profile.birthDay, M:(profile.birthMonth - 1), y:profile.birthYear}));
        }

        // On qa and dev some users have not migrated and have a 5 letter code such as en_de;
        this.set('language', (profile.languages ? profile.languages.substring(0, 2) : ''));
        this.set('country', profile.country);
        this.set('state', profile.state);
        this.set('isVerified', isVerified);
        this.set('emails', emails);
        this.get('registrationSource', data.bc_registrationSource);

        if (profile.phones && profile.phones.length) {

            profile.phones.forEach((phone) => this.setPhone(phone));
        }

        try {

            let accessApplications = JSON.parse(data.bc_accessApplications);

            this.set('isScoutingUser', !!_.find(accessApplications, {application: 'SCOUTING'}));

        } catch (e) {

            this.set('isScoutingUser', false);
        }

        this.pushUserIdToGa();
    }

    pushUserIdToGa () {

        /**
         * Setting google analytics USER_ID for session unification.
         * Bayer users should not be tracked personally according to working council requirements.
         */
        if (ENV === 'production' &&
            _.isFunction(window.ga) &&
            this.get('email').indexOf('@bayer.com') === -1 &&
            this.get('email').indexOf('@grr.la') === -1 &&
            this.get('email').indexOf('@pokemail.net') === -1 &&
            this.get('email').indexOf('@sharklasers.com') === -1) {

            window.ga('set', 'userId', this.get('uuid'));
        }
    }

    /**
     * Universal callback for processing GIGYA's responses.
     *
     * @param {object}     response  Response object to analyze.
     * @param {$.Deferred} promise   Promise to be resolved/rejected on success/fail.
     * @param {boolean}    isPiped   Flag to prevent resolving the given promise on current step.
     */
    onGigyaResponse (response, promise, isPiped = false) {

        if (response.errorCode === 0 && !isPiped) {

            promise.resolve(this);
        }
        else {

            let errMessage = GigyaUtils.getGigyaErrorMessage(response);

            if (errMessage) {
                promise.reject(errMessage);
            }
        }
    }

    setPhone (phone) {

        if (phone && phone.type && phone.number) {

            switch (phone.type) {

                case 'MOBILE': {

                    this.set('mobilePhone', phone.number);
                    break;
                }

                case 'HOME': {

                    this.set('phone', phone.number);
                    break;
                }
            }
        }
    }

    saveProfileInfo () {

        let birthYear;

        if (this.get('dob') && moment.isMoment(this.get('dob'))) {
            birthYear = this.get('dob').year();
        }

        return this.get('gigya')
            .setAccountInfo({
                UID: this.get('uuid'),
                profile: {
                    firstName: this.get('firstName'),
                    lastName: this.get('lastName'),
                    address: this.get('address'),
                    city: this.get('location'),
                    zip: this.get('zip'),
                    birthYear,
                    phones: [
                        {
                            type: 'MOBILE',
                            number: this.get('mobilePhone')
                        },
                        {
                            type: 'HOME',
                            number: this.get('phone')
                        }
                    ],
                    state: this.get('state')
                },
                data: {
                    bc_taxID: this.get('taxId'),
                    bc_registrationSource: this.get('registrationSource')
                }
            });
    }

    logout () {
        let options = {};

        // removing DF_TOKEN cookie from second level domain
        if (ENV !== 'local') {
            options = {
                domain: TOKEN_DOMAIN
            };
        }

        // expire second-level domain
        CookieReader.expire(SECURITY_TOKEN_NAME, options);

        // expire third-level domain (legacy)
        CookieReader.expire(SECURITY_TOKEN_NAME);

        Cache.uuid = null;
        return this.get('gigya').logout({UID: this.get('uuid')});
    }

    renew () {

        this.set(this.defaults());
        this._depsFetched = null;
        this._profileFetched = null;
        this.resetTutorialFlags();
    }

    /**
     * Fetch settings and distributors
     *
     * @returns $.Deferred()
     *
     * @memberOf UserModel
     */
    fetchDeps () {

        let country = this.getCountry();

        if (country) {

            this.get('applicationSettings').getFilterParams().set({
                $country: country,
                objectType: undefined
            });
        }

        this._depsFetched = $.when(
            this.get('settings').update(),
            this.get('distributors').update({}, true),
            this.get('applicationSettings').update({}, true),
            this.getLatestTermsAndConditions()
        ).done(() => {
            if(ENV === 'demo') {
                this.get('applicationSettings').country = 'de';
            } else {
                this.get('applicationSettings').country = this.get('token').get('decoded').country;
            }
        });

        return this._depsFetched;
    }

    getLatestTermsAndConditions () {

        let path = '/docs/hash.json';
        let country = this.getCountry();

        this._latestTermsAndConditions = [];

        return UserModel.getTermsAndConditionsData(path)
            .then((data) => {

            this._latestTermsAndConditions = _.pickBy(data['tc'], (value, key) => {
                return _.endsWith(key, '_'+country);
            });

            return this._latestTermsAndConditions;
        }, () => {
            return this._latestTermsAndConditions;
        });
    }

    /**
     * @description This method is used to refetch the user settings clearing and existing ones
     *              if it is not part of the response.
     *
     * @memberOf UserModel
     */
    clearAndFetchSettings () {

        return this.get('settings').update({remove: true});
    }

    /**
     * Fetch user profile from gigya
     *
     * @param {any} parameters - see gigya.accounts.getAccountInfo
     * @returns $.Deferred()
     *
     * @memberOf UserModel
     */
    fetchProfile (parameters) {

        this._profileFetched = this.get('gigya').getAccountInfo(parameters);

        return this._profileFetched;
    }

    exchangeGigyaToken (gigyaToken) {

        this.attributes.token.set('gigyaToken', gigyaToken);

        return this.attributes.token.exchangeGigyaToken();
    }

    isTermsAccepted () {

        return true; // DFITE-19631

        /*let tcFlag = this.get('settings').getSetting('TAMS', 'IS_T&C_ACCEPTED');

        return (tcFlag !== null && _.includes(_.values(this.latestTermsAndConditions), tcFlag.get('value')));*/
    }

    isAdditionalInfoRequired () {

        let aiFlag = this.get('settings').getSetting('TAMS', 'IS_ADDITIONAL_INFO_REQUIRED');

        return (aiFlag !== null && aiFlag.get('value') === 'TRUE');
    }

    isDistributorAcceptanceRequired () {

        let distSetting = this.get('settings').getSetting('TAMS', 'IS_DISTRIBUTOR_ACTIVE');
        let distributors = this.get('distributors');

        return (distSetting && distributors.length && distSetting.get('value') !== 'TRUE' && distSetting.get('value') !== 'FALSE');
    }

    static validatePassword (password, options) {
        return UserPasswordValidationModel.validatePassword(password, options);
    }

    requestProfile () {

        if(this.attributes.uuid || this.attributes.email) {

            let userFilter = (this.attributes.uuid ?
                ('?uuid=["' + this.attributes.uuid + '"]') :
                ('?email=["' + this.attributes.email.toLowerCase() + '"]'));

            let fetched = $.Deferred();

            $.ajax({
                url: (API_PREFIX + '/' + this.microservice + '/' + this.resource + userFilter),
                type: 'GET',
                dataType: 'json',
                contentType: 'application/json'
            }).done((response) => {

                if (!_.isArray(response) || !response.length) {

                    fetched.reject(this);
                    return false;
                }

                let profile = _.head(response);

                this.set('uuid', profile.uuid);
                this.set('email', profile.email);
                this.set('lastName', profile.lastName);
                this.set('firstName', profile.firstName);

                fetched.resolve(this);
            });

            return fetched;
        } else {
            return $.Deferred().reject();
        }
    }

    /**
     * @description Sets UUID of the user as a Sentry object property to make it available in error traces.
     */
    setRavenUserContext () {

        let ravenContext = Raven.getContext();

        if (!ravenContext.user || !ravenContext.user.uuid) {
            Raven.setUserContext({
                uuid: this.get('uuid')
            });
        }
    }

    static validateFirstName (name, options) {

        const field = 'firstName';
        const messageInvalid = __('NAME_INVALID_CHAR');
        const messageRequired = __('FIRST_NAME_IS_REQUIRED');

        return UserModel.validateName(name, options, field, messageInvalid, messageRequired);
    }

    static validateLastName (name, options, country) {

        const field = 'lastName';
        const messageInvalid = __('NAME_INVALID_CHAR');
        const messageRequired = __('LAST_NAME_IS_REQUIRED');

        if (!name && options && options.isHf) {

            return {
                property: 'lastName',
                message: __('LAST_NAME_IS_REQUIRED')
            }
        }

        if(CountryUtils.COUNTRY.CANADA === country || CountryUtils.COUNTRY.USA === country) {
            return UserModel.validateName(name, options, field, messageInvalid, messageRequired);
        }

        return UserModel.validateName(name, options, field, messageInvalid);
    }

    static validateName (name = "", options, field, messageInvalid, messageRequired) {

        if (messageRequired && !name) {

            return {
                property: field,
                message: messageRequired
            };
        }

        if (!messageRequired && !name) {

            return;
        }

        let max = 256;

        if(name.length > max) {

            return {
                property: field,
                message: __('NAME_TOO_LONG')
            };
        }

        // making name check based on the country does not make sense,
        // because we can have a polish farmer in germany
        let globalNameRegExp = RegexUtils.getUserNameRegex();

        if (!(globalNameRegExp.test(name))) {

            return {
                property: field,
                message: messageInvalid
            };
        }
    }

    static validateCountry (country) {

        if(!country){
            return {
                property: 'country',
                message: __('COUNTRY_IS_REQUIRED')
            };
        }

        let countries = _.values(CountryUtils.COUNTRY);

        if(!_.includes(countries, country)){
            return {
                property: 'country',
                message: __('COUNTRY_IS_REQUIRED')
            };
        }
    }

    static validateLanguage (language) {

        if(!language){
            return {
                property: 'language',
                message: __('LANGUAGE_IS_REQUIRED')
            };
        }

        let languages = _.values(CountryUtils.LANGUAGE);

        if(!_.includes(languages, language)){
            return {
                property: 'language',
                message: __('LANGUAGE_IS_REQUIRED')
            };
        }
    }

    static validateEmail (email, property = 'email', options) {

        let emailRegexp = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

        if (!email) {

            const emailRequiredMsg = options && options.emailRequiredMsg || __('EMAIL_IS_REQUIRED');

            return {
                property: property,
                message: emailRequiredMsg
            };
        }

        // The maximum total length of a reverse-path or forward-path is 256 characters.
        if (!_.isString(email) || email.length > 256 || email.search(emailRegexp) === -1) {

            return {
                property: property,
                message: __('EMAIL_INVALID')
            };
        }
    }

    static validateDob (dob, property = 'dob') {

        if (!dob) {

            return {
                property,
                message: ''
            };
        }
    }

    static validateZip (zip, property = 'zip', options = {country: CountryUtils.COUNTRY.GERMANY}) {

        if (zip) {

            let country = options ? options.country : '';

            switch (country) {

                case CountryUtils.COUNTRY.ARGENTINA: {

                    let arZipRegExp = /^[0-9]{4}$/;

                    if (!(arZipRegExp.test(zip))) {

                        return {
                            property,
                            message: __('ZIP_INVALID_AR')
                        };
                    }
                    break;
                }

                case CountryUtils.COUNTRY.AUSTRIA: {

                    let atZipRegExp = /^[0-9]{4}$/;

                    if (!(atZipRegExp.test(zip))) {

                        return {
                            property,
                            message: __('ZIP_INVALID_AT')
                        };
                    }
                    break;
                }

                case CountryUtils.COUNTRY.BRAZIL: {

                    let brZipRegExp = /^[0-9]{5}(-?)[0-9]{3}$/;

                    if (!(brZipRegExp.test(zip))) {

                        return {
                            property,
                            message: __('ZIP_INVALID_BR')
                        };
                    }

                    break;
                }

                case CountryUtils.COUNTRY.CANADA: {

                    let caZipRegExp = /^[A-Za-z]{1}[0-9]{1}[A-Za-z]{1}(\s?)[0-9]{1}[A-Za-z]{1}[0-9]{1}$/;

                    if (!(caZipRegExp.test(zip))) {

                        return {
                            property,
                            message: __('ZIP_INVALID_CA')
                        };
                    }

                    break;
                }

                case CountryUtils.COUNTRY.NETHERLAND: {

                    let nlZipRegExp = /^[0-9]{4}(\s?)[A-Za-z]{2}$/;

                    if (!(nlZipRegExp.test(zip))) {

                        return {
                            property,
                            message: __('ZIP_INVALID_NL')
                        };
                    }

                    break;
                }

                case CountryUtils.COUNTRY.POLAND: {

                    let plZipRegExp = /^[0-9]{2}(-?)[0-9]{3}$/;

                    if (!(plZipRegExp.test(zip))) {

                        return {
                            property,
                            message: __('ZIP_INVALID_PL')
                        };
                    }

                    break;
                }

                case CountryUtils.COUNTRY.UKRAINE: {

                    let uaZipRegExp = /^[0-9]{5}$/;

                    if (!(uaZipRegExp.test(zip))) {

                        return {
                            property,
                            message: __('ZIP_INVALID_UA')
                        };
                    }

                    break;
                }

                case CountryUtils.COUNTRY.BELGIUM: {

                    let beZipRegExp = /^[0-9]{4}$/;

                    if (!(beZipRegExp.test(zip))) {

                        return {
                            property,
                            message: __('ZIP_INVALID_BE')
                        };
                    }

                    break;
                }

                case CountryUtils.COUNTRY.GREAT_BRITAIN: {

                    let gbZipRegExp = /^[A-Za-z]{1}([A-Za-z]?)[0-9]{1}([0-9A-Za-z]?)(\s?)[0-9]{1}[A-Za-z]{2}$/;

                    if (!(gbZipRegExp.test(zip))) {

                        return {
                            property,
                            message: __('ZIP_INVALID_GB')
                        };
                    }

                    break;
                }

                case CountryUtils.COUNTRY.HUNGARY: {

                    let huZipRegExp = /^[0-9]{4}$/;

                    if (!(huZipRegExp.test(zip))) {

                        return {
                            property,
                            message: __('ZIP_INVALID_HU')
                        };
                    }

                    break;
                }

                case CountryUtils.COUNTRY.CZECH_REPUBLIC: {

                    let czZipRegExp = /^[0-9]{3}(\s?)[0-9]{2}$/;

                    if (!(czZipRegExp.test(zip))) {

                        return {
                            property,
                            message: __('ZIP_INVALID_CZ')
                        };
                    }

                    break;
                }

                case CountryUtils.COUNTRY.RUSSIA: {

                    let ruZipRegExp = /^[0-9]{6}$/;

                    if (!(ruZipRegExp.test(zip))) {

                        return {
                            property,
                            message: __('ZIP_INVALID_DEFAULT')
                        };
                    }

                    break;
                }

                case CountryUtils.COUNTRY.JAPAN: {

                    let jaZipRegExp = /^[0-9]{3}(-?)[0-9]{4}$/;

                    if (!(jaZipRegExp.test(zip))) {

                        return {
                            property,
                            message: __('ZIP_INVALID_JP')
                        };
                    }

                    break;
                }

                default: {

                    if (String(zip).search(/^[0-9]{5}$/) === -1) {

                        return {
                            property,
                            message: __('ZIP_INVALID_DEFAULT')
                        };
                    }
                }
            }
        }
    }

    static validateLocation (location, property = 'location') {

        if (location) {

            let trimmed = _.trim(location);

            if (trimmed.length < 2) {

                return {
                    property: property,
                    message: __('LOCATION_TOO_SHORT')
                };
            }

            if(trimmed.length > 256) {

                return {
                    property: property,
                    message: __('LOCATION_TOO_LONG')
                };
            }
        }
    }

    static validateAddress (address, attr, options) {

        if (!address && options && options.isHf) {

            return {
                property: 'address',
                message: __('ADDRESS_IS_REQUIRED')
            }
        }

        if (address) {

            let trimmed = _.trim(address);

            if (trimmed.length < 2) {

                return {
                    property: 'address',
                    message: __('ADDRESS_TOO_SHORT')
                };
            }

            if(trimmed.length > 256) {

                return {
                    property: 'address',
                    message: __('ADDRESS_TOO_LONG')
                };
            }
        }
    }

    static validateBusinessName (businessName, attr, options) {

        if (options && options.isHf) {


            if (!businessName) {

                return {
                    property: 'businessName',
                    message: __('BUSINESS_NAME_IS_REQUIRED')
                }
            }
    
            if (businessName) {
    
                let trimmed = _.trim(businessName);
    
                if (trimmed.length < 2) {
    
                    return {
                        property: 'businessName',
                        message: __('BUSINESS_NAME_TOO_SHORT')
                    };
                }
    
                if(trimmed.length > 256) {
    
                    return {
                        property: 'businessName',
                        message: __('BUSINESS_NAME_TOO_LONG')
                    };
                }
            }
        }
    }

    static validateMobilePhone (phone) {

        if (phone) {

            let phoneRegexp = /^[0-9()\-+ ]{8,20}$/;

            // Accepts minimum 8 maximum 20 characters with at least 8 digits.
            // Special characters that are allowed are +-() and blank space
            if (!(_.isString(phone) && phone.replace(/[^0-9]/g, '').length >= 8 && phoneRegexp.test(phone))) {

                return {
                    property: 'mobilePhone',
                    message: __('MOBILE_PHONE_INVALID')
                };
            }
        }
    }

    static validatePhone (phone, property = 'phone', options) {

        if (!phone && options && options.isHf) {

            return {
                property: 'phone',
                message: __('PHONE_INVALID')
            }
        }

        if (phone) {

            let validateMsg = UserModel.validateMobilePhone(phone);

            if (validateMsg) {

                validateMsg.property = property;
                validateMsg.message = __('PHONE_INVALID');

                return validateMsg;
            }
        }
    }

    static validateTaxId (taxId, countryId) {

        if (taxId) {

            //BR tax id CPF format: 235-628-758-27 or 235.628.758.27
            let taxIdCpfRegExp = /^([0-9]{3}[-.]?){3}[0-9]{2}$/;
            //BR tax id CNPJ format: 13.339.532/0001-09
            let taxIdCnpjRegExp = /^[0-9]{2}[-.]?[0-9]{3}[-.]?[0-9]{3}[/]?[0-9]{4}[-.]?[0-9]{2}$/;

            if (!(_.isString(taxId) && (taxIdCpfRegExp.test(taxId) || taxIdCnpjRegExp.test(taxId))) ||
                (countryId && (countryId === CountryUtils.COUNTRY.BRAZIL) && !UserModel.validaCpfCnpj(taxId))) {

                return {
                    property: 'taxId',
                    message: __('TAX_ID_INVALID')
                };
            }
        }
    }

    // Copied from https://irias.com.br/blog/como-validar-cpf-cnpj-em-node-js/ @reference provided by umit
    static validaCpfCnpj (val) {

        if (val.length == 14) {
            let cpf = val.trim();

            cpf = cpf.replace(/\./g, '');
            cpf = cpf.replace('-', '');
            cpf = cpf.split('');

            let v1 = 0;
            let v2 = 0;
            let aux = false;

            for (let i = 1; cpf.length > i; i++) {
                if (cpf[i - 1] != cpf[i]) {
                    aux = true;
                }
            }

            if (aux == false) {
                return false;
            }

            for (let i = 0, p = 10; (cpf.length - 2) > i; i++, p--) {
                v1 += cpf[i] * p;
            }

            v1 = ((v1 * 10) % 11);

            if (v1 == 10) {
                v1 = 0;
            }

            if (v1 != cpf[9]) {
                return false;
            }

            for (let i = 0, p = 11; (cpf.length - 1) > i; i++, p--) {
                v2 += cpf[i] * p;
            }

            v2 = ((v2 * 10) % 11);

            if (v2 == 10) {
                v2 = 0;
            }

            if (v2 != cpf[10]) {
                return false;
            } else {
                return true;
            }
        } else if (val.length == 18) {
            let cnpj = val.trim();

            cnpj = cnpj.replace(/\./g, '');
            cnpj = cnpj.replace('-', '');
            cnpj = cnpj.replace('/', '');
            cnpj = cnpj.split('');

            let v1 = 0;
            let v2 = 0;
            let aux = false;

            for (let i = 1; cnpj.length > i; i++) {
                if (cnpj[i - 1] != cnpj[i]) {
                    aux = true;
                }
            }

            if (aux == false) {
                return false;
            }

            for (let i = 0, p1 = 5, p2 = 13; (cnpj.length - 2) > i; i++, p1--, p2--) {
                if (p1 >= 2) {
                    v1 += cnpj[i] * p1;
                } else {
                    v1 += cnpj[i] * p2;
                }
            }

            v1 = (v1 % 11);

            if (v1 < 2) {
                v1 = 0;
            } else {
                v1 = (11 - v1);
            }

            if (v1 != cnpj[12]) {
                return false;
            }

            for (let i = 0, p1 = 6, p2 = 14; (cnpj.length - 1) > i; i++, p1--, p2--) {
                if (p1 >= 2) {
                    v2 += cnpj[i] * p1;
                } else {
                    v2 += cnpj[i] * p2;
                }
            }

            v2 = (v2 % 11);

            if (v2 < 2) {
                v2 = 0;
            } else {
                v2 = (11 - v2);
            }

            if (v2 != cnpj[13]) {
                return false;
            } else {
                return true;
            }
        } else {
            return false;
        }
    }

    static get ACCESS_LEVEL () {

        return {
            DISTRIBUTOR_READ: 'DISTRIBUTOR_READ',
            DISTRIBUTOR: 'DISTRIBUTOR_READ',
            WRITE: 'WRITE',
            SHARE: 'SHARE',
            READ: 'READ',
            CONTRACTOR_LIMITED_FARM_ACCESS: 'CONTRACTOR_LIMITED_FARM_ACCESS',
        };
    }

    static get REG_SOURCE () {

        return {
            WEB: 'WEB',
            MOBILE: 'MOBILE',
            SCOUTING: 'SCOUTING'
        };
    }


    static getTermsAndConditionsData(path) {

        if(ENV === 'demo') {
            let deferred = new $.Deferred();

            require.ensure(['../../../js/resources/DemoData'], (require) => {
                let demoData = require('../../../js/resources/DemoData').DemoData;

                deferred.resolve(demoData.getHashData());
            });

            return deferred;

        } else {
            return $.ajax({
                url: path,
                type: 'GET',
                dataType: 'json'
            });
        }
    }

    /**
     * @description Non Bayer accounts (other than bayer and/or guirella mail) from Germany and France are disabled.
     * */
    isDisabledUser () {

        return false;
    }

    getCountry () {

        try {
            if(ENV === 'demo') {
                return 'de';
            } else {
                return this.get('token').get('decoded').country;
            }
        } catch (error) {
            return;
        }
    }

    /**
     * @description This method check country from the token and return true if country belongs to France
     *
     * @returns {Boolean} true|false
     */
    isFrenchUser () {

        return this.getCountry() === CountryUtils.COUNTRY.FRANCE;
    }

    /**
     * @description This method check country from the token and return true if country belongs to France
     *
     * @returns {Boolean} true|false
     */
    isJapaneseUser () {

        return this.getCountry() === CountryUtils.COUNTRY.JAPAN;
    }

    /**
     * @description This method check country from the token and return true if country belongs to Austria
     *
     * @returns {Boolean} true|false
     */
    isAustrianUser () {

        return this.getCountry() === CountryUtils.COUNTRY.AUSTRIA;
    }

    /**
     * @description This method check country from the token and return true if country belongs to Brazil
     *
     * @returns {Boolean} true|false
     */
    isBrazilUser () {

        return this.getCountry() === CountryUtils.COUNTRY.BRAZIL;
    }

    /**
     * @description This method check country from the token and return true if country belongs to Ukraine
     *
     * @returns {Boolean} true|false
     */
    isUkraineUser () {

        return this.getCountry() === CountryUtils.COUNTRY.UKRAINE;
    }

    /**
     * @description This method check country from the token and return true if country belongs to Canada
     *
     * @returns {Boolean} true|false
     */
    isCanadianUser () {

        return this.getCountry() === CountryUtils.COUNTRY.CANADA;
    }

    /**
     * @description This method check country from the token and return true if country belongs to USA
     *
     * @returns {Boolean} true|false
     */
    isUSAUser () {

        return this.getCountry() === CountryUtils.COUNTRY.USA;
    }

    /**
     * @description This method check country from the token and return true if country belongs to North America
     *
     * @returns {Boolean} true|false
     */
    isNorthAmericaUser () {

        return this.isCanadianUser() || this.isUSAUser();
    }

    /**
     * @description This method check country from the token and return true if country belongs to Argentina
     *
     * @returns {Boolean} true|false
     */
    isArgentinianUser () {

        return this.getCountry() === CountryUtils.COUNTRY.ARGENTINA;
    }

    /**
     * @description This method check country from the token and return true if country belongs to Belgium
     *
     * @returns {Boolean} true|false
     */
    isBelgianUser () {

        return this.getCountry() === CountryUtils.COUNTRY.BELGIUM;
    }

    /**
     * @description This method check country from the token and return true if country belongs to Great Britian
     *
     * @returns {Boolean} true|false
     */
    isGreatBritainUser () {

        return this.getCountry() === CountryUtils.COUNTRY.GREAT_BRITAIN;
    }

    /**
     * @description This method check country from the token and return true if country belongs to Hungary
     *
     * @returns {Boolean} true|false
     */
    isHungarianUser () {

        return this.getCountry() === CountryUtils.COUNTRY.HUNGARY;
    }

    isGermanUser () {

        return this.getCountry() === CountryUtils.COUNTRY.GERMANY;
    }

    /**
     * @description This method check country from the token and return true if country belongs to Czech Republic
     *
     * @returns {Boolean} true|false
     */
    isCzechRepublicUser () {

        return this.getCountry() === CountryUtils.COUNTRY.CZECH_REPUBLIC;
    }

    /**
     * @description This method check country from the token and return true if country belongs to Netherland
     *
     * @returns {Boolean} true|false
     */
    isNetherlandUser () {

        return this.getCountry() === CountryUtils.COUNTRY.NETHERLAND;
    }

    /**
     * @description This method compares the users country with the application country.
     *              If they don't match, it forces the application to reload in the users country
     *              and preffered language.
     *
     * @returns {Boolean} true|false - Based on whether redirection was done or not
     */
    checkCountryMismatch (appCountry, appLocale) {

        let userCountry = this.get('country');
        let userLanguage = this.get('language');

        // make sure that the app can support users language
        let config = _.find(CountryUtils.getSupportedCountries(),
            (config) => (config.country === userCountry));

        if(!_.includes(config.languages, userLanguage)){
            userLanguage = config.languages[0];
        }

        if(appCountry && (userCountry !== appCountry)) {

            let redirectUrl = document.location.href
                .replace(`/${appCountry}/${appLocale}`,
                    `/${userCountry}/${userLanguage}_${userCountry}`);

            document.location.href = redirectUrl;

            return true;
        }

        return false;
    }

    /**
     * Shortcut to check app feature for the user
     *
     * @param {string} feature
     * @returns {boolean}
     */
    isFeatureEnabled (feature) {
        if (!this.appFeatures) {
            console.warn('No AppFeatures in User');
            return false;
        }
        return this.appFeatures.isFeatureEnabled({feature, country: this.getCountry()});
    }

    /**
     * @description This method check the existence of uuid,
     *              if uuid exists then its assumed that user is logged in otherwise logged out.
     *
     * @returns {Boolean} true|false
     */
    isUserLoggedIn () {

        return (this.get('uuid') !== '');
    }


    /**
     * Show tutorial
     * 1. once per 24 hours
     * 2. after logout/login
     *
     * @param {string} flag
     * @returns {boolean}
     */
    shouldShowTutorial (flag) {
        if (!flag) {
            console.warn('tutorial flag required');
            return;
        }

        const ONE_DAY = 1000*60*60*24;
        let lsKey = 'TUTORIAL_' + flag.toUpperCase();
        let lsTutorial = JSON.parse(localStorage.getItem(lsKey) || '{}');
        let noFlagSaved = !lsTutorial.ts;
        let moreThanDay = Date.now() - lsTutorial.ts > ONE_DAY;

        if (noFlagSaved || moreThanDay) {
            try {
                localStorage.setItem(lsKey, JSON.stringify({ts: Date.now()}));
            } catch (e) {
                console.warn(e);
            }
            return true;
        }
    }

    /**
     * Show tutorial only once per 24h
     * @returns {boolean}
     */
    shouldShowFieldTutorial () {
        return this.shouldShowTutorial('field');
    }

    /**
     * Show tutorial only once per 24h
     * @returns {boolean}
     */
    shouldShowCropSeasonTutorial () {
        return this.shouldShowTutorial('crop_season');
    }

    /**
     * Show tutorial only once per 24h
     * @returns {boolean}
     */
    shouldShowHealthyFieldsTutorial () {
        return this.shouldShowTutorial('healthy_fields');
    }

    /**
     * Reset tutorial timeouts on user logout
     */
    resetTutorialFlags () {
        ['field', 'crop_season', 'healthy_fields'].forEach((item) => {
            localStorage.removeItem('TUTORIAL_' + item.toUpperCase());
        });
    }


    verifyPassword (email) {

        return this.get('gigya').login({
            loginID: email,
            password: this.get('password'),
            loginMode: 'reAuth'
        });
    }

    updateEmail () {

        return $.ajax({
            url: `${API_PREFIX}/users/users/${this.get('uuid')}/accounts/`,
            type: 'POST',
            dataType: 'json',
            contentType: 'application/json',
            data: JSON.stringify({
                login: this.get('email')
            }),
            headers: {
                'IGNORE_HTTP_ERROR': [400, 500]
            }
        })
        .done(() => {
            this.trigger('update:Email');
        });
    }

    isWeedWidgetEnabled(cropUuid) {

        return (this.isBrazilUser()
            && (cropUuid === CropUtils.CROPS.SOYBEAN
                || cropUuid === CropUtils.CROPS.CORN
                || cropUuid || CropUtils.CROPS.GOSSYPIUM_HIRSUTUM))
            || (this.isArgentinianUser()
                && (cropUuid === CropUtils.CROPS.SOYBEAN
                    || cropUuid === CropUtils.CROPS.CORN));
    }

    isGrowthStageObservationRequired(cropUuid) {

        return (cropUuid === CropUtils.CROPS.CANOLA || cropUuid === CropUtils.CROPS.SOYBEAN || (this.isArgentinianUser() && CropUtils.CROPS.CORN === cropUuid));
    }

    isAddDroneTaskEnabled(cropUuid) {
        return (this.isBrazilUser() && cropUuid === CropUtils.CROPS.SOYBEAN);
    }

    isWeedInArgentina(cropUuid) {
        return this.isArgentinianUser() && (cropUuid === CropUtils.CROPS.SOYBEAN || cropUuid === CropUtils.CROPS.CORN);
    }

    getTwoLetterName () {

        let firstName = this.get('firstName');
        let lastName = this.get('lastName');

        return `${firstName.charAt(0).toUpperCase()}${lastName ? lastName.charAt(0).toUpperCase() : ''}`;
    }

    createFirstFarm () {

        let deferred = $.Deferred();

        let farm = new FarmModel({
            name : __('SOMEONES_FARM').replace('<name>', this.get('firstName')),
            creatorUuid : this.get('uuid'),
            access : 'SHARE'
        });

        farm.save()
            .done(() => {
                deferred.resolve(farm);
            })
            .fail(() => {
                deferred.reject();
            });

        return deferred;
    }
}



// WEBPACK FOOTER //
// ./shared/model/users/UserModel.js